import { LinkButton, getFrontendConfigValue, usePath } from '@arnold/common';
import styled from '@emotion/styled';
import { FC, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import {
  ProcessStatus,
  TopicGroupTypeCode,
  useCreateSurveyGroupMutation,
  useTopicGroupQuery,
  useUserQuery,
} from '../../generated/hooks';
import { getAllowedTopicGroups, getOrganizationStructure, getSurveyGroups } from '../../graphql/queries';
import ee from '../../lib/eventEmitter';
import { Card } from '../Common';
import { PageLayout } from '../Common/PageLayout';
import GraphQLErrorCard from '../GraphQLErrorCard';
import Loading from '../Loading';
import { RenameProcessSurveyForm } from '../RenameProcessSurveyForm';
import { StepsEditTable } from '../StepsEditData';

type NewProcessSurveyProps = {
  title: string;
  topicGroupId: string;
  typeCode?: TopicGroupTypeCode;
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FormContainer = styled.div`
  width: 70%;
`;

export const NewProcessSurvey: FC<NewProcessSurveyProps> = ({ title, topicGroupId, typeCode }) => {
  const { i18n, t } = useTranslation('surveyGroup');
  const { t: topicT } = useTranslation('topicOverview');

  const {
    loading: userLoading,
    data: userData,
    error: userError,
  } = useUserQuery({
    variables: {
      languageCode: i18n.language,
    },
  });

  const { loading, error, data } = useTopicGroupQuery({
    variables: { topicGroupId },
  });

  const { conversationSubjectsPath, onboardingPath, exitPath, processSurveyDetailPath, processSurveysPath } = usePath();

  const isOnboarding = typeCode === TopicGroupTypeCode.Onboarding;
  const isExit = typeCode === TopicGroupTypeCode.Exit;

  const [name, setName] = useState<string>(data?.topicGroup.name || '');
  const [createSurveyGroup] = useCreateSurveyGroupMutation();
  const history = useHistory();

  const scheduleSurveyGroup = async () => {
    if (userData?.user.organization?.id) {
      // Show a loading overlay while the survey group is being created and the
      // related queries are refetched (can take a few seconds), so the user gets
      // immediate feedback after confirming instead of a blank pause.
      ee.emitModalLoading();
      try {
        const result = await createSurveyGroup({
          variables: {
            input: {
              name,
              topicGroupId,
              organizationId: userData?.user.organization?.id,
            },
          },
          refetchQueries: [
            {
              query: getOrganizationStructure,
              variables: { organizationId: userData?.user.organization?.id },
            },
            {
              query: getSurveyGroups,
            },
            {
              query: getAllowedTopicGroups,
              variables: { languageCode: i18n.language },
            },
          ],
          awaitRefetchQueries: true,
        });
        const surveyGroupId = result.data?.createSurveyGroup.id;
        if (surveyGroupId) {
          const path = isOnboarding
            ? onboardingPath.createPath(surveyGroupId)
            : isExit
              ? exitPath.createPath(surveyGroupId)
              : processSurveyDetailPath.createPath(surveyGroupId);
          history.push(path);
        }
      } catch (error) {
        // do nothing
      } finally {
        ee.emitModalLoadingDismiss();
      }
    }
  };

  const handleSubmit = async () => {
    const hasNoSurveys = !data?.topicGroup.steps?.length;
    if (hasNoSurveys) {
      ee.emitModalScheduleEmptySurveyGroup(scheduleSurveyGroup);
      return;
    }
    await scheduleSurveyGroup();
  };

  useEffect(() => {
    const surveyGroups =
      (userData &&
        userData?.user?.organization?.surveyGroups &&
        userData?.user?.organization.surveyGroups.filter(
          (sg) =>
            (sg.topicGroup.id === topicGroupId || sg.topicGroup.parentTopicGroup?.id === topicGroupId) &&
            [ProcessStatus.Prepared, ProcessStatus.Ongoing].includes(sg.status),
        )) ||
      [];
    if (surveyGroups.length > 0) {
      ee.emitSgAlreadyExistsModal(
        surveyGroups.length === 1
          ? () => {
              const path = isOnboarding
                ? onboardingPath.createPath(surveyGroups[0].id)
                : isExit
                  ? exitPath.createPath(surveyGroups[0].id)
                  : processSurveyDetailPath.createPath(surveyGroups[0].id);
              history.push(path);
            }
          : () => window.location.replace(`${getFrontendConfigValue('SELF_CARE_URL')}/overview`),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(userData)]);

  useEffect(() => {
    const translatedTitle = data?.topicGroup.translations?.find((tran) => tran.language.code === i18n.language)?.value;
    setName(translatedTitle || data?.topicGroup.name || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  if (loading || userLoading || !data) {
    return <Loading />;
  }

  if (error || userError) {
    return <GraphQLErrorCard />;
  }

  const dataIcom = isOnboarding ? 'heading-new-onboarding' : isExit ? 'heading-new-exit' : 'heading-new-process-survey';
  return (
    <PageLayout
      title={title}
      dataIcom={dataIcom}
      breadcrumbItems={[
        {
          title: `< ${t('changeTopic')}`,
          link: isOnboarding
            ? conversationSubjectsPath.createPath(TopicGroupTypeCode.Onboarding)
            : isExit
              ? conversationSubjectsPath.createPath(TopicGroupTypeCode.Exit)
              : conversationSubjectsPath.createPath(),
        },
      ]}
    >
      <Card>
        <Container>
          <FormContainer>
            <RenameProcessSurveyForm name={name} setName={setName} isOnboarding={isOnboarding} />
          </FormContainer>
          <LinkButton
            onClick={() => {
              history.push(`/process/${topicGroupId}`);
            }}
          >
            {topicT('detailLink')}
          </LinkButton>
        </Container>
      </Card>
      {data?.topicGroup && (
        <StepsEditTable steps={data.topicGroup.steps} processTopicGroupId={topicGroupId} hideActions />
      )}

      <Button
        color="primary"
        onClick={handleSubmit}
        data-icom={'btn-schedule-survey'}
        data-cy={'btn-schedule-process-survey'}
      >
        {t('save')}
      </Button>
      <Button
        color="primary"
        variant={'outline-primary'}
        href={
          isOnboarding ? onboardingPath.createPath() : isExit ? exitPath.createPath() : processSurveysPath.createPath()
        }
        data-icom={'btn-cancel'}
      >
        {t('cancel')}
      </Button>
    </PageLayout>
  );
};
